/**
 * Writing Studio Types
 *
 * Data model for projects, chapters, and notes.
 * Designed for future-proofing with chapters array from day one.
 */

// Project modes
export type ProjectMode = 'learn' | 'write'
export type ProjectStatus = 'draft' | 'in_progress' | 'completed' | 'archived'

/**
 * A Writing Studio project (local storage shape)
 */
export interface LocalProject {
  id: string
  childId: string
  title: string
  coverEmoji: string
  mode: ProjectMode
  status: ProjectStatus
  wordCount: number
  activeChapterId: string | null
  createdAt: string
  updatedAt: string
  lastExportedAt: string | null
  chapters: LocalChapter[]
  notes: string
}

/**
 * A chapter within a project
 */
export interface LocalChapter {
  id: string
  title: string
  order: number
  summary: string | null
  content: string
  plaintextPreview: string
  wordCount: number
  createdAt: string
  updatedAt: string
}

/**
 * Highlight (Slice 3 - sidecar, not embedded in content)
 */
export interface Highlight {
  id: string
  chapterId: string
  start: number
  end: number
  color: 'yellow' | 'green' | 'pink'
  createdAt: string
}

/**
 * Local storage state
 */
export const LOCAL_WRITING_STUDIO_VERSION = 1

export interface LocalWritingStudioState {
  version: 1
  childId: string
  projects: LocalProject[]
}

export const WRITING_STUDIO_STORAGE_KEYS = {
  getStudioKey: (childId: string) => `inkwelljr:v1:child:${childId}:writingStudio`,
} as const

/**
 * Domain types (for repository interface)
 */
export interface ProjectRecord {
  id: string
  childId: string
  title: string
  coverEmoji: string
  mode: ProjectMode
  status: ProjectStatus
  wordCount: number
  activeChapterId: string | null
  createdAt: string
  updatedAt: string
  lastExportedAt: string | null
}

export interface ChapterRecord {
  id: string
  projectId: string
  title: string
  order: number
  summary: string | null
  content: string
  plaintextPreview: string
  wordCount: number
  createdAt: string
  updatedAt: string
}

export interface ProjectNoteRecord {
  id: string
  projectId: string
  content: string
  updatedAt: string
}

export interface ProjectWithChapters {
  project: ProjectRecord
  chapters: ChapterRecord[]
  notes: string
}

/**
 * Export document structure
 */
export interface ExportDocument {
  title: string
  chapters: Array<{
    title: string
    contentPlaintext: string
  }>
  exportedAt: string
}

/**
 * Utility functions
 */
export function calculateWordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length
}

export function generatePlaintextPreview(content: string, maxLength = 200): string {
  const plaintext = content.trim()
  if (plaintext.length <= maxLength) return plaintext
  return plaintext.slice(0, maxLength).trim() + '...'
}

/**
 * Random emoji covers for new projects
 */
export const PROJECT_EMOJIS = [
  '📝', '✨', '🌟', '📖', '🦋', '🌈', '🏰', '🐉',
  '🦄', '🌸', '🎭', '🎪', '🚀', '🌙', '🎨', '🎵',
]

export function getRandomProjectEmoji(): string {
  return PROJECT_EMOJIS[Math.floor(Math.random() * PROJECT_EMOJIS.length)]
}
